import { SidebarLeft, XMark } from "@medusajs/icons";
import { Dialog as DialogPrimitive, Slot } from "radix-ui";
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { cn } from "../lib/cn";
import { IconButton } from "./button";

interface ShellState {
  /** Closes the slide-over navigation on a phone. Call it after navigating. */
  closeMobileNav: () => void;
  /** True inside the frame, so a screen knows it sits beside the navigation. */
  inShell: boolean;
}

const ShellContext = createContext<ShellState>({ closeMobileNav: () => {}, inShell: false });

export const useShell = () => useContext(ShellContext);

const RAIL_KEY = "lumberset.sidebar.hidden";

/**
 * The frame every signed-in screen sits in: navigation rail on the left, a
 * slim top bar, and a scrolling content area. On a phone the rail becomes a
 * slide-over opened from the top bar.
 */
export function AppShell({
  sidebar,
  breadcrumbs,
  actions,
  banner,
  children,
}: {
  sidebar: ReactNode;
  breadcrumbs?: ReactNode;
  /** Right side of the top bar: notifications, help. */
  actions?: ReactNode;
  /** Across the very top, above the navigation and every screen: a `Banner` for a state nobody should lose sight of. */
  banner?: ReactNode;
  children: ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [railHidden, setRailHidden] = useState(() => {
    try {
      return window.localStorage.getItem(RAIL_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(RAIL_KEY, railHidden ? "1" : "0");
    } catch {
      // Private browsing can refuse storage. The preference just does not persist.
    }
  }, [railHidden]);

  const closeMobileNav = useCallback(() => setMobileOpen(false), []);
  const state = useMemo(() => ({ closeMobileNav, inShell: true }), [closeMobileNav]);

  const toggle = () => {
    if (window.matchMedia("(min-width: 1024px)").matches) setRailHidden((hidden) => !hidden);
    else setMobileOpen(true);
  };

  return (
    <ShellContext.Provider value={state}>
      <div className="flex h-dvh w-full flex-col overflow-hidden bg-ui-bg-subtle">
        {banner}
        <div className="flex min-h-0 w-full flex-1 overflow-hidden">
          <aside
            className={cn(
              "hidden h-full w-[220px] shrink-0 flex-col border-r border-ui-border-base bg-ui-bg-subtle lg:flex",
              railHidden && "lg:hidden",
            )}
          >
            {sidebar}
          </aside>

          <DialogPrimitive.Root open={mobileOpen} onOpenChange={setMobileOpen}>
            <DialogPrimitive.Portal>
              <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ui-bg-overlay data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 lg:hidden" />
              <DialogPrimitive.Content
                aria-describedby={undefined}
                className="pt-safe pb-safe fixed inset-y-2 left-2 z-50 flex w-[min(300px,calc(100vw-4rem))] flex-col overflow-hidden rounded-xl bg-ui-bg-subtle shadow-elevation-modal outline-none duration-200 ease-out-expo data-[state=open]:animate-in data-[state=open]:slide-in-from-left data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left lg:hidden"
              >
                <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
                <DialogPrimitive.Close asChild>
                  <IconButton aria-label="Close navigation" className="absolute right-2 top-3 z-10">
                    <XMark />
                  </IconButton>
                </DialogPrimitive.Close>
                {sidebar}
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          </DialogPrimitive.Root>

          <div className="flex h-full min-w-0 flex-1 flex-col">
            <header className="pt-safe shrink-0 border-b border-ui-border-base bg-ui-bg-subtle">
              <div className="flex h-12 items-center justify-between gap-x-3 px-3">
                <div className="flex min-w-0 items-center gap-x-1.5">
                  <IconButton aria-label="Toggle navigation" onClick={toggle}>
                    <SidebarLeft />
                  </IconButton>
                  <div className="min-w-0">{breadcrumbs}</div>
                </div>
                <div className="flex shrink-0 items-center gap-x-1.5">{actions}</div>
              </div>
            </header>
            <main className="flex-1 overflow-y-auto overscroll-contain">
              <div className="pb-safe mx-auto flex w-full max-w-[1600px] flex-col gap-y-3 p-3">
                {children}
              </div>
            </main>
          </div>
        </div>
      </div>
    </ShellContext.Provider>
  );
}

/* ---- Pieces the sidebar is assembled from -------------------------------- */

function SidebarHeader({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("p-3", className)} {...props} />;
}

function SidebarBody({ className, ...props }: ComponentProps<"nav">) {
  return (
    <nav
      className={cn("flex flex-1 flex-col gap-y-0.5 overflow-y-auto px-3 pb-3", className)}
      {...props}
    />
  );
}

function SidebarFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("flex flex-col gap-y-0.5 border-t border-ui-border-base p-3", className)}
      {...props}
    />
  );
}

function SidebarLabel({ className, ...props }: ComponentProps<"p">) {
  return (
    <p
      className={cn("txt-compact-xsmall-plus px-2 pb-1 pt-4 text-ui-fg-muted", className)}
      {...props}
    />
  );
}

export const navItemStyles = cn(
  "txt-compact-small-plus transition-fg group flex w-full cursor-pointer select-none items-center gap-x-2 rounded-md py-0.5 pl-0.5 pr-2 text-left text-ui-fg-subtle outline-none",
  "hover:bg-ui-bg-subtle-hover focus-visible:shadow-borders-focus",
  // Finger sized rows on touch screens.
  "pointer-coarse:py-2",
  "[&>svg]:m-[4.5px] [&>svg]:shrink-0 [&>svg]:text-ui-fg-subtle",
  // TanStack Router marks the current link with aria-current="page".
  "aria-[current=page]:bg-ui-bg-base aria-[current=page]:text-ui-fg-base aria-[current=page]:shadow-elevation-card-rest aria-[current=page]:hover:bg-ui-bg-base",
  "data-[active=true]:bg-ui-bg-base data-[active=true]:text-ui-fg-base data-[active=true]:shadow-elevation-card-rest",
);

/**
 * One row of navigation. Wrap a router link with `asChild` and put the icon
 * first inside it: `<NavItem asChild><Link to="/projects"><Buildings /> Projects</Link></NavItem>`.
 */
function NavItem({
  asChild = false,
  className,
  onClick,
  ...props
}: ComponentProps<"a"> & { asChild?: boolean }) {
  const { closeMobileNav } = useShell();
  const Tag = asChild ? Slot.Root : "a";
  return (
    <Tag
      className={cn(navItemStyles, className)}
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        closeMobileNav();
      }}
      {...props}
    />
  );
}

export const Sidebar = {
  Header: SidebarHeader,
  Body: SidebarBody,
  Footer: SidebarFooter,
  Label: SidebarLabel,
  Item: NavItem,
};

/* ---- Breadcrumbs --------------------------------------------------------- */

export function Breadcrumbs({ className, ...props }: ComponentProps<"ol">) {
  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol
        className={cn(
          "txt-compact-small-plus flex min-w-0 items-center gap-x-1 text-ui-fg-muted",
          "[&>li+li]:before:mr-1 [&>li+li]:before:text-ui-fg-disabled [&>li+li]:before:content-['›']",
          "[&>li:last-child]:truncate [&>li:last-child]:text-ui-fg-subtle [&>li]:flex [&>li]:min-w-0 [&>li]:items-center",
          // On a phone only the current location fits.
          "max-sm:[&>li:not(:last-child)]:hidden max-sm:[&>li]:before:hidden",
          "[&_a]:transition-fg [&_a]:rounded [&_a]:outline-none [&_a]:hover:text-ui-fg-subtle [&_a]:focus-visible:shadow-borders-focus",
          className,
        )}
        {...props}
      />
    </nav>
  );
}
